import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { CalendarCheck, Search, RefreshCw, FileX, FileCheck, ScrollText, Pencil, UserCog } from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import LogViewerDialog from "../components/LogViewerDialog";
import { statusToCode } from "../lib/attendanceStatus";

const STATUS_COLORS = {
  "Present": "bg-emerald-100 text-emerald-800",
  "Half Day": "bg-amber-100 text-amber-800",
  "Leave": "bg-sky-100 text-sky-800",
  "Holiday": "bg-violet-100 text-violet-800",
  "Weekend": "bg-indigo-100 text-indigo-800",
  "Absent": "bg-rose-100 text-rose-800",
  "Not Marked": "bg-slate-100 text-slate-600",
};
const ALL_STATUSES = ["Present", "Half Day", "Leave", "Holiday", "Weekend", "Absent", "Not Marked"];

const toIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return "—"; }
};

// Render geo as "City, Region · Country" if available.
const fmtGeo = (geo) => {
  if (!geo || typeof geo !== "object") return "";
  const parts = [geo.city, geo.region, geo.country].filter((p) => p && String(p).trim());
  return parts.join(" · ");
};

function PunchMeta({ location, ip, geo, label }) {
  const geoLabel = fmtGeo(geo);
  if (!location && !ip && !geoLabel) return null;
  return (
    <div className="mt-1 space-y-0.5 text-[9.5px] leading-tight" data-testid={`ta-punch-meta-${label}`}>
      {location && (
        <div className="text-slate-700 truncate max-w-[180px]" title={location}>
          <span className="text-slate-400">Status:</span> {location}
        </div>
      )}
      {ip && (
        <div className="mono text-slate-500" title={ip}>
          <span className="text-slate-400">IP:</span> {ip}
        </div>
      )}
      {geoLabel && (
        <div className="text-slate-500 truncate max-w-[180px]" title={geoLabel}>
          <span className="text-slate-400">Geo:</span> {geoLabel}
        </div>
      )}
    </div>
  );
}

export default function TeamAttendance() {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const isMasterAdmin = user?.role === "master_admin";

  const [mode, setMode] = useState("day"); // day | range | month | user
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const todayIso = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().slice(0, 10); });
  const [dateTo, setDateTo] = useState(todayIso);
  const [employeeId, setEmployeeId] = useState("");

  const [data, setData] = useState({ rows: [], summary: {} });
  const [rangeData, setRangeData] = useState({ rows: [], dates: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reportFilter, setReportFilter] = useState("all");

  // Editor designation + logs
  const [editor, setEditor] = useState({ editor_id: "", editor_name: "" });
  const [showEditor, setShowEditor] = useState(false);
  const [editorSearch, setEditorSearch] = useState("");
  const [showLogs, setShowLogs] = useState(false);
  const [editing, setEditing] = useState(null); // { employee_id, date, status, remark }

  const canEdit = isMasterAdmin || (editor.editor_id && user?.id === editor.editor_id);

  const fetchEditor = async () => {
    try { const { data: e } = await api.get("/admin/team-attendance/editor"); setEditor(e || {}); } catch { /* noop */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      if (mode === "day") {
        const { data: res } = await api.get(`/admin/team-attendance?date=${date}`);
        setData(res);
      } else {
        let from = dateFrom, to = dateTo;
        if (mode === "month") {
          const m = date.slice(0, 7);
          from = `${m}-01`;
          const end = new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)), 0).toISOString().slice(0, 10);
          to = end;
        }
        const params = new URLSearchParams({ date_from: from, date_to: to });
        if (mode === "user" && employeeId) params.set("employee_id", employeeId);
        const { data: res } = await api.get(`/admin/team-attendance?${params.toString()}`);
        setRangeData(res);
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); fetchEditor(); }, [mode, date, dateFrom, dateTo, employeeId]);

  const filteredRows = useMemo(() => {
    let list = data.rows || [];
    const q = search.toLowerCase().trim();
    if (q) list = list.filter((r) => [r.name, r.email, r.employee_code, r.role].some((v) => String(v || "").toLowerCase().includes(q)));
    if (statusFilter !== "all") list = list.filter((r) => r.status === statusFilter);
    if (reportFilter === "missing") list = list.filter((r) => r.report_missing);
    else if (reportFilter === "submitted") list = list.filter((r) => r.report_submitted);
    return list;
  }, [data, search, statusFilter, reportFilter]);

  const s = data.summary || {};

  const saveStatus = async () => {
    if (!editing) return;
    try {
      await api.patch("/admin/team-attendance/status", editing);
      toast.success("Status updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveEditor = async (uid) => {
    try {
      await api.put("/admin/team-attendance/editor", { user_id: uid });
      toast.success("Editor updated");
      setShowEditor(false);
      fetchEditor();
    } catch (e) { toast.error(formatError(e)); }
  };

  const [usersForPick, setUsersForPick] = useState([]);
  useEffect(() => {
    if (!showEditor) return;
    api.get("/records/users_list").catch(() => api.get("/admin/users-list"))
      .then(({ data: d }) => setUsersForPick(d || []))
      .catch(() => {
        // Fallback — derive from current data.rows
        setUsersForPick((data.rows || []).map((r) => ({ id: r.employee_id, name: r.name, email: r.email, role: r.role })));
      });
  }, [showEditor, data]);

  return (
    <div className="max-w-[1400px] space-y-6">
      <PageHeader
        moduleKey="team_attendance"
        tone="blue"
        icon={CalendarCheck}
        eyebrow="Workspace"
        title="Team Attendance"
        subtitle="Day / range / month / user view · IST timezone · Edits flow to Payroll."
        actions={
          <div className="flex items-center gap-2">
            {editor.editor_id ? (
              <Badge className="bg-violet-100 text-violet-800" data-testid="ta-editor-tag">
                Designated Editor: {editor.editor_name || "—"}
              </Badge>
            ) : (
              <Badge variant="outline">No editor designated</Badge>
            )}
            {isMasterAdmin && (
              <Button size="sm" variant="outline" onClick={() => setShowEditor(true)} data-testid="ta-set-editor">
                <UserCog size={12} className="mr-1" /> Designate Editor
              </Button>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => setShowLogs(true)} data-testid="ta-logs-btn">
                <ScrollText size={12} className="mr-1" /> Edit Logs
              </Button>
            )}
          </div>
        }
      />

      {/* Mode + filter toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1" data-testid="ta-mode">
          {[["day", "Date"], ["range", "Range"], ["month", "Month"], ["user", "User"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} className={`px-3 py-1.5 text-[12px] font-semibold rounded border ${mode === k ? "bg-violet-600 text-white border-violet-600" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
              data-testid={`ta-mode-${k}`}>{l}</button>
          ))}
        </div>
        {mode === "day" && (<Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44" data-testid="ta-date" />)}
        {mode === "month" && (<Input type="month" value={date.slice(0, 7)} onChange={(e) => setDate(e.target.value + "-01")} className="h-9 w-44" data-testid="ta-month" />)}
        {(mode === "range" || mode === "user") && (
          <>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="ta-from" />
            <span className="text-slate-400">→</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="ta-to" />
          </>
        )}
        {mode === "user" && (
          <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="h-9 px-2 border rounded text-[12px]" data-testid="ta-user">
            <option value="">— Pick employee —</option>
            {(data.rows || []).map((r) => <option key={r.employee_id} value={r.employee_id}>{r.name}</option>)}
          </select>
        )}
        <Button variant="outline" size="sm" onClick={load} className="h-9" data-testid="ta-refresh"><RefreshCw size={12} className="mr-1" /> Refresh</Button>
        <div className="relative flex-1 min-w-[180px] max-w-md ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, code…" className="pl-9 h-9" data-testid="ta-search" />
        </div>
      </div>

      {/* DAY MODE — summary + per-employee table */}
      {mode === "day" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="ta-summary">
            {[
              { k: "total", label: "Total", c: "bg-slate-50 text-slate-700" },
              { k: "present", label: "Present", c: "bg-emerald-50 text-emerald-800" },
              { k: "half_day", label: "Half Day", c: "bg-amber-50 text-amber-800" },
              { k: "on_leave", label: "Leave", c: "bg-sky-50 text-sky-800" },
              { k: "absent", label: "Absent", c: "bg-rose-50 text-rose-800" },
              { k: "report_missing", label: "Report Missing", c: "bg-rose-100 text-rose-900 border-rose-200" },
            ].map((t) => (
              <div key={t.k} className={`p-3 rounded-md border ${t.c}`}>
                <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{t.label}</div>
                <div className="text-2xl font-extrabold mono mt-1">{s[t.k] ?? 0}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {["all", "Present", "Half Day", "Leave", "Absent", "Not Marked"].map((k) => (
              <button key={k} onClick={() => setStatusFilter(k)}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border ${statusFilter === k ? "bg-violet-600 text-white border-violet-600" : "bg-white border-slate-200 text-slate-600"}`}
                data-testid={`ta-status-${k}`}>{k === "all" ? "All" : k}</button>
            ))}
            {[["all", "All"], ["missing", "Report Missing"], ["submitted", "Report Submitted"]].map(([k, l]) => (
              <button key={k} onClick={() => setReportFilter(k)}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border ${reportFilter === k ? "bg-rose-600 text-white border-rose-600" : "bg-white border-slate-200 text-slate-600"}`}
                data-testid={`ta-report-${k}`}>{l}</button>
            ))}
          </div>

          <div className={viewMode === "card" ? "" : "pk-card overflow-x-auto"}>
            {viewMode === "card" ? (
              loading ? (
                <div className="pk-card p-12 text-center text-slate-400">Loading…</div>
              ) : filteredRows.length === 0 ? (
                <div className="pk-card p-12 text-center text-slate-400">No rows</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="ta-cards">
                  {filteredRows.map((r) => (
                    <div key={r.employee_id} className={`pk-card p-4 hover:shadow-md transition-shadow ${r.report_missing ? "border-rose-200 bg-rose-50/30" : ""}`} data-testid={`ta-card-${r.employee_id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 truncate">{r.name}</div>
                          <div className="text-[10.5px] mono text-slate-500 truncate">{r.employee_code || "—"} · {r.role || "—"}</div>
                        </div>
                        <Badge className={(STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700") + " text-[10px] shrink-0 gap-1"}>
                          <span className="font-mono font-bold">{statusToCode(r.status, r)}</span>
                          <span className="opacity-60">|</span>
                          {r.status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Check In</div><div className="mono text-slate-800 text-[10.5px]">{toIST(r.check_in)}</div></div>
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Check Out</div><div className="mono text-slate-800 text-[10.5px]">{toIST(r.check_out)}</div></div>
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Hours</div><div className="mono font-semibold text-slate-900">{r.hours_worked ? Number(r.hours_worked).toFixed(2) : "—"}</div></div>
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Report</div>
                          {r.report_submitted
                            ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]"><FileCheck size={9} className="mr-1" /> Submitted</Badge>
                            : r.report_missing ? <Badge className="bg-rose-100 text-rose-800 text-[10px]"><FileX size={9} className="mr-1" /> Missing</Badge>
                            : <span className="text-slate-400 text-[10px]">N/A</span>}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="pt-2 border-t border-slate-100 flex justify-end">
                          <Button size="sm" variant="outline" onClick={() => setEditing({ employee_id: r.employee_id, date, status: r.status, remark: "" })} className="h-7 text-[10.5px]" data-testid={`ta-edit-card-${r.employee_id}`}>
                            <Pencil size={10} className="mr-1" /> Edit
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : (
            <table className="w-full text-sm responsive-table" data-testid="ta-table">
              <thead className="bg-slate-50/80 border-b">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Code</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Check In (IST)</th>
                  <th className="px-4 py-3 font-semibold">Check Out (IST)</th>
                  <th className="px-4 py-3 font-semibold">Hours</th>
                  <th className="px-4 py-3 font-semibold text-center">Report</th>
                  {canEdit && <th className="px-4 py-3 font-semibold text-right"></th>}
                </tr>
              </thead>
              <tbody>
                {loading && (<tr><td colSpan={9} className="text-center py-12 text-slate-400">Loading…</td></tr>)}
                {!loading && filteredRows.length === 0 && (<tr><td colSpan={9} className="text-center py-12 text-slate-400">No rows</td></tr>)}
                {!loading && filteredRows.map((r) => (
                  <tr key={r.employee_id} className={`border-b last:border-0 hover:bg-slate-50/60 ${r.report_missing ? "bg-rose-50/30" : ""}`} data-testid={`ta-row-${r.employee_id}`}>
                    <td className="px-4 py-3 font-semibold">{r.name}</td>
                    <td className="px-4 py-3 mono text-[11px] text-slate-500">{r.employee_code || "—"}</td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">{r.role || "—"}</td>
                    <td className="px-4 py-3">
                      <Badge className={(STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700") + " gap-1"}>
                        <span className="font-mono font-bold">{statusToCode(r.status, r)}</span>
                        <span className="opacity-60">|</span>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 mono text-[11px] align-top">
                      <div>{toIST(r.check_in)}</div>
                      <PunchMeta label={`in-${r.employee_id}`} location={r.check_in_location} ip={r.check_in_ip} geo={r.check_in_geo} />
                    </td>
                    <td className="px-4 py-3 mono text-[11px] align-top">
                      <div>{toIST(r.check_out)}</div>
                      <PunchMeta label={`out-${r.employee_id}`} location={r.check_out_location} ip={r.check_out_ip} geo={r.check_out_geo} />
                    </td>
                    <td className="px-4 py-3 mono">{r.hours_worked ? Number(r.hours_worked).toFixed(2) : "—"}</td>
                    <td className="px-4 py-3 text-center">
                      {r.report_submitted
                        ? <Badge className="bg-emerald-100 text-emerald-800"><FileCheck size={11} className="mr-1" /> Submitted</Badge>
                        : r.report_missing ? <Badge className="bg-rose-100 text-rose-800"><FileX size={11} className="mr-1" /> Missing</Badge>
                        : <span className="text-slate-400 text-[10px]">N/A</span>}
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => setEditing({ employee_id: r.employee_id, date, status: r.status, remark: "" })} data-testid={`ta-edit-${r.employee_id}`}>
                          <Pencil size={11} />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        </>
      )}

      {/* RANGE / MONTH / USER MODE — per-day matrix */}
      {mode !== "day" && (
        <div className="pk-card overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="ta-matrix">
            <thead className="bg-slate-50/80 border-b sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 font-semibold">Employee</th>
                {(rangeData.dates || []).map((d) => (<th key={d} className="px-2 py-2 font-semibold mono">{d.slice(5)}</th>))}
                <th className="px-3 py-2 font-semibold text-right">P/A/L/HD</th>
                <th className="px-3 py-2 font-semibold text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={2 + (rangeData.dates || []).length + 1} className="py-12 text-center text-slate-400">Loading…</td></tr>)}
              {!loading && (rangeData.rows || []).length === 0 && (<tr><td className="py-12 text-center text-slate-400">No data</td></tr>)}
              {!loading && (rangeData.rows || []).map((r) => (
                <tr key={r.employee_id} className="border-b last:border-0 hover:bg-slate-50/60">
                  <td className="px-3 py-1.5 font-semibold text-slate-800 sticky left-0 bg-white">{r.name}</td>
                  {r.days.map((d) => {
                    const inGeo = fmtGeo(d.check_in_geo);
                    const outGeo = fmtGeo(d.check_out_geo);
                    const meta = [
                      d.check_in && `In: ${toIST(d.check_in)}${d.check_in_location ? ` (${d.check_in_location})` : ""}${d.check_in_ip ? ` · ${d.check_in_ip}` : ""}${inGeo ? ` · ${inGeo}` : ""}`,
                      d.check_out && `Out: ${toIST(d.check_out)}${d.check_out_location ? ` (${d.check_out_location})` : ""}${d.check_out_ip ? ` · ${d.check_out_ip}` : ""}${outGeo ? ` · ${outGeo}` : ""}`,
                    ].filter(Boolean).join("\n");
                    return (
                    <td key={d.date} className="px-1 py-1.5 text-center">
                      <button
                        disabled={!canEdit}
                        onClick={() => setEditing({ employee_id: r.employee_id, date: d.date, status: d.status, remark: "" })}
                        className={`inline-block w-7 h-7 rounded text-[9px] font-bold ${d.status === "Present" ? "bg-emerald-100 text-emerald-800" : d.status === "Half Day" ? "bg-amber-100 text-amber-800" : d.status === "Leave" ? "bg-sky-100 text-sky-800" : d.status === "Holiday" ? "bg-violet-100 text-violet-800" : d.status === "Absent" ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-600"} ${canEdit ? "hover:ring-2 hover:ring-violet-300 cursor-pointer" : "cursor-default"}`}
                        title={`${d.date} · ${d.status}${d.hours ? ` · ${d.hours}h` : ""}${meta ? `\n${meta}` : ""}`}
                        data-testid={`ta-cell-${r.employee_id}-${d.date}`}
                      >
                        {d.status === "Present" ? "P" : d.status === "Half Day" ? "½" : d.status === "Leave" ? "L" : d.status === "Holiday" ? "H" : d.status === "Absent" ? "A" : "—"}
                      </button>
                    </td>
                    );
                  })}
                  <td className="px-3 py-1.5 mono text-right text-[10.5px]">
                    {r.totals.present}/{r.totals.absent}/{r.totals.leave}/{r.totals.half_day}
                  </td>
                  <td className="px-3 py-1.5 mono text-right font-semibold">{r.totals.hours.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Status Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-md">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle>Edit Attendance Status</DialogTitle>
                <div className="text-[11px] text-slate-500 mono">{editing.date}</div>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-[10px] uppercase font-semibold text-slate-500">Status</Label>
                  <div className="flex gap-1 flex-wrap mt-1">
                    {ALL_STATUSES.map((st) => (
                      <button key={st} type="button" onClick={() => setEditing({ ...editing, status: st })}
                        className={`px-3 py-1.5 text-[11px] font-semibold rounded border ${editing.status === st ? STATUS_COLORS[st] + " border-current" : "bg-white border-slate-200 text-slate-600"}`}
                        data-testid={`ta-edit-status-${st}`}>{st}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-semibold text-slate-500">Remark (optional)</Label>
                  <Input value={editing.remark} onChange={(e) => setEditing({ ...editing, remark: e.target.value })} placeholder="Why this change?" data-testid="ta-edit-remark" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveStatus} data-testid="ta-edit-save">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Designate Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Designate Team Attendance Editor</DialogTitle>
            <div className="text-[11px] text-slate-500">Only the designated user (and you) can edit attendance status for everyone.</div>
          </DialogHeader>
          <Input value={editorSearch} onChange={(e) => setEditorSearch(e.target.value)} placeholder="Search by name…" className="mb-2" data-testid="ta-editor-search" />
          <div className="space-y-1">
            <button onClick={() => saveEditor("")} className="w-full text-left px-3 py-2 text-[12px] border rounded hover:bg-slate-50 text-rose-700" data-testid="ta-editor-clear">
              ✗ Clear designation
            </button>
            {((data.rows || []).filter((r) => !editorSearch || r.name.toLowerCase().includes(editorSearch.toLowerCase()))).map((u) => (
              <button key={u.employee_id} onClick={() => saveEditor(u.employee_id)} className={`w-full text-left px-3 py-2 text-[12px] border rounded hover:bg-violet-50 ${editor.editor_id === u.employee_id ? "bg-violet-50 border-violet-300" : ""}`}
                data-testid={`ta-editor-pick-${u.employee_id}`}>
                <div className="font-semibold">{u.name}</div>
                <div className="text-[10px] text-slate-500">{u.email} · {u.role}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <LogViewerDialog
        open={showLogs}
        onClose={() => setShowLogs(false)}
        title="Team Attendance Edit Logs"
        endpoint="/admin/team-attendance/logs"
        revertEndpointBuilder={canEdit ? (lg) => `/admin/team-attendance/logs/${lg.id}/revert` : null}
      />

      {/* Comp-Off Approvals — moved from Payroll. Approvers see punch in/out & hours
          so they can decide 0.5 or 1 day comp-off. */}
      {(isMasterAdmin || canEdit) && <CompOffApprovalsBlock />}

      {/* Comp-Off → Earned Leave conversion — also moved from Payroll. Allows HR /
          Master Admin to convert an employee's accumulated comp-off into earned leave. */}
      {(isMasterAdmin || canEdit) && <CompOffConversionBlock />}

      {/* Leave Balance Management — full balance manager (Casual / Sick / Earned)
          embedded here so HR / Master can adjust balances without leaving the page. */}
      {(isMasterAdmin || canEdit) && <LeaveBalanceBlock />}
    </div>
  );
}

function CompOffConversionBlock() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState({});
  const [busyAll, setBusyAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/comp-off-balances"); setRows(data || []); }
    catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const convert = async (row) => {
    if (!window.confirm(`Convert ${row.comp_off_balance} comp-off day(s) of ${row.employee_name} into Earned Leave?`)) return;
    setBusy((b) => ({ ...b, [row.employee_id]: true }));
    try {
      const { data } = await api.post(`/employees/${row.employee_id}/convert-comp-off`);
      toast.success(`Converted ${data.converted} day(s) → Earned Leave`);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy((b) => ({ ...b, [row.employee_id]: false })); }
  };

  const convertAll = async () => {
    if (rows.length === 0) return;
    const totalDays = rows.reduce((s, r) => s + Number(r.comp_off_balance || 0), 0);
    if (!window.confirm(
      `Convert ALL comp-off balances → Earned Leave?\n\n` +
      `${rows.length} employee${rows.length === 1 ? "" : "s"} · ${totalDays} total day${totalDays === 1 ? "" : "s"}.\n\n` +
      `This action cannot be undone.`
    )) return;
    setBusyAll(true);
    try {
      const { data } = await api.post("/comp-off-balances/convert-all");
      toast.success(`Converted ${data.total_days} day(s) for ${data.converted_count} employee(s) → Earned Leave`);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusyAll(false); }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.employee_name || "").toLowerCase().includes(s)
      || (r.employee_code || "").toLowerCase().includes(s)
      || (r.department || "").toLowerCase().includes(s);
  });

  return (
    <div className="pk-card p-4 mt-6" data-testid="ta-compoff-conversion">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h2 className="heading text-lg font-bold text-slate-900">Convert Comp-Off → Earned Leave</h2>
          <div className="text-[10.5px] text-slate-500 mt-0.5">
            HR / Master Admin only · Adds the full comp-off balance to the employee&apos;s earned leave and resets comp-off to 0.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee…" className="h-8 w-52 text-[12px]" data-testid="ta-co-conv-search" />
          <Button onClick={load} size="sm" variant="outline" className="h-8 text-[11px]" data-testid="ta-co-conv-reload">
            <RefreshCw size={12} className="mr-1" /> Refresh
          </Button>
          <Button onClick={convertAll} disabled={busyAll || rows.length === 0} size="sm"
            className="h-8 text-[11px] bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="ta-co-conv-all">
            {busyAll ? "Converting…" : `Convert All (${rows.length})`}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/60">
            <tr>
              <th className="px-3 py-2 text-left pk-table-header">Employee</th>
              <th className="px-3 py-2 text-left pk-table-header">Department</th>
              <th className="px-3 py-2 text-left pk-table-header">Comp-Off Balance</th>
              <th className="px-3 py-2 text-left pk-table-header">Earned Leave</th>
              <th className="px-3 py-2 text-right pk-table-header">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center py-6 text-slate-400 text-sm">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400 text-sm" data-testid="ta-co-conv-empty">
                No employees have a comp-off balance to convert.
              </td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr key={r.employee_id} className="border-b border-slate-100" data-testid={`ta-co-conv-row-${r.employee_id}`}>
                <td className="px-3 py-2">
                  <div className="font-semibold">{r.employee_name}</div>
                  <div className="text-[10px] mono text-slate-500">{r.employee_code || "—"}</div>
                </td>
                <td className="px-3 py-2 text-[12px] text-slate-600">{r.department || "—"}</td>
                <td className="px-3 py-2">
                  <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 text-[10.5px]" data-testid={`ta-co-conv-comp-${r.employee_id}`}>
                    {r.comp_off_balance} day{r.comp_off_balance === 1 ? "" : "s"}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10.5px]" data-testid={`ta-co-conv-earned-${r.employee_id}`}>
                    {r.earned_leave_balance} day{r.earned_leave_balance === 1 ? "" : "s"}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button onClick={() => convert(r)} disabled={busy[r.employee_id]} size="sm" variant="outline"
                    className="h-7 text-[10.5px]" data-testid={`ta-co-conv-btn-${r.employee_id}`}>
                    → Earned Leave
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompOffApprovalsBlock() {
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState({});
  const load = async () => {
    try { const { data } = await api.get("/comp-off-requests"); setReqs(data || []); }
    catch (e) { void e; }
  };
  useEffect(() => { load(); }, []);

  const decide = async (r, decision, approved_days) => {
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const body = { decision };
      if (decision === "Approved") body.approved_days = approved_days;
      await api.post(`/comp-off-requests/${r.id}/decide`, body);
      toast.success(decision === "Approved" ? `Approved ${approved_days} day${approved_days === 1 ? "" : "s"}` : "Rejected");
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy((b) => ({ ...b, [r.id]: false })); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }); }
    catch { return iso; }
  };

  const pending = reqs.filter((r) => r.status === "Pending");
  const decided = reqs.filter((r) => r.status !== "Pending");

  return (
    <div className="pk-card p-4 mt-6" data-testid="ta-compoff-approvals">
      <div className="flex items-center justify-between mb-3">
        <h2 className="heading text-lg font-bold text-slate-900">
          Comp-Off Approvals
          {pending.length > 0 && (
            <span className="ml-2 text-[10.5px] font-bold bg-rose-600 text-white rounded-full px-2 py-0.5">{pending.length} pending</span>
          )}
        </h2>
        <div className="text-[10.5px] text-slate-500">
          ≥ 8h30m worked → 1 day · &lt; 8h30m → 0.5 day (HR can override)
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/60">
            <tr>
              <th className="px-3 py-2 text-left pk-table-header">Employee</th>
              <th className="px-3 py-2 text-left pk-table-header">Date</th>
              <th className="px-3 py-2 text-left pk-table-header">Punch In</th>
              <th className="px-3 py-2 text-left pk-table-header">Punch Out</th>
              <th className="px-3 py-2 text-left pk-table-header">Hours</th>
              <th className="px-3 py-2 text-left pk-table-header">Reason</th>
              <th className="px-3 py-2 text-right pk-table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pending.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-400 text-sm">No pending comp-off requests.</td></tr>}
            {pending.map((r) => {
              const hours = Number(r.hours_worked || 0);
              const defaultDays = hours >= 8.5 ? 1 : 0.5;
              return (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`ta-co-req-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{r.employee_name}</div>
                    <div className="text-[10px] mono text-slate-500">{r.employee_code || "—"}</div>
                  </td>
                  <td className="px-3 py-2 mono text-[12px]">{r.date}</td>
                  <td className="px-3 py-2 mono text-[12px]">{fmtTime(r.check_in)}</td>
                  <td className="px-3 py-2 mono text-[12px]">{fmtTime(r.check_out)}</td>
                  <td className="px-3 py-2 mono text-[12px] font-semibold">{hours ? hours.toFixed(2) : "—"}</td>
                  <td className="px-3 py-2 text-[12px]">{r.reason}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1.5 flex-wrap">
                      <Button onClick={() => decide(r, "Approved", 0.5)} disabled={busy[r.id]} size="sm"
                        variant={defaultDays === 0.5 ? "default" : "outline"}
                        className={`h-7 text-[10.5px] ${defaultDays === 0.5 ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                        data-testid={`ta-co-approve-half-${r.id}`}>
                        Approve 0.5
                      </Button>
                      <Button onClick={() => decide(r, "Approved", 1)} disabled={busy[r.id]} size="sm"
                        variant={defaultDays === 1 ? "default" : "outline"}
                        className={`h-7 text-[10.5px] ${defaultDays === 1 ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                        data-testid={`ta-co-approve-full-${r.id}`}>
                        Approve 1
                      </Button>
                      <Button onClick={() => decide(r, "Rejected")} disabled={busy[r.id]} size="sm" variant="outline"
                        className="h-7 text-[10.5px] text-rose-600 border-rose-200" data-testid={`ta-co-reject-${r.id}`}>
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {decided.length > 0 && (
        <details className="mt-3 text-[11.5px]">
          <summary className="text-slate-500 cursor-pointer">History ({decided.length})</summary>
          <ul className="mt-2 space-y-1">
            {decided.slice(0, 20).map((r) => (
              <li key={r.id} className="flex items-center gap-2 text-slate-700">
                <Badge className={r.status === "Approved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>{r.status}</Badge>
                <span><b>{r.employee_name}</b> · {r.date}</span>
                {r.status === "Approved" && <span className="mono">{r.approved_days || 1} day</span>}
                <span className="text-slate-400">— {r.decided_by_name}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


function LeaveBalanceBlock() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ casual: 0, sick: 0, earned: 0 });
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/leave-balances"); setRows(data || []); }
    catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      casual: r.leave_balance?.casual ?? 0,
      sick: r.leave_balance?.sick ?? 0,
      earned: r.leave_balance?.earned ?? 0,
    });
  };

  const save = async () => {
    if (!editing) return;
    setBusy((b) => ({ ...b, [editing.id]: true }));
    try {
      await api.patch(`/employees/${editing.id}/leave-balance`, {
        casual: Number(form.casual),
        sick: Number(form.sick),
        earned: Number(form.earned),
      });
      toast.success(`Updated balance for ${editing.name}`);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy((b) => ({ ...b, [editing?.id]: false })); }
  };

  const reset = async (r) => {
    if (!window.confirm(`Reset Casual / Sick / Earned balance to 0 for ${r.name}?`)) return;
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      await api.delete(`/employees/${r.id}/leave-balance`);
      toast.success(`Balance reset for ${r.name}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy((b) => ({ ...b, [r.id]: false })); }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [r.name, r.email, r.employee_code, r.department, r.role]
      .some((v) => String(v ?? "").toLowerCase().includes(s));
  });

  const totalCasual = rows.reduce((s, r) => s + (r.leave_balance?.casual || 0), 0);
  const totalSick = rows.reduce((s, r) => s + (r.leave_balance?.sick || 0), 0);
  const totalEarned = rows.reduce((s, r) => s + (r.leave_balance?.earned || 0), 0);

  return (
    <div className="pk-card p-4 mt-6" data-testid="ta-leave-balance">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h2 className="heading text-lg font-bold text-slate-900">Leave Balance Management</h2>
          <div className="text-[10.5px] text-slate-500 mt-0.5">
            HR / Master Admin only · Edit Casual / Sick / Earned balances per employee · Totals across all employees:&nbsp;
            <span className="font-semibold text-slate-700">Casual {totalCasual}</span> ·&nbsp;
            <span className="font-semibold text-slate-700">Sick {totalSick}</span> ·&nbsp;
            <span className="font-semibold text-slate-700">Earned {totalEarned}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / code / dept…" className="h-8 w-56 text-[12px]" data-testid="ta-lb-search" />
          <Button onClick={load} size="sm" variant="outline" className="h-8 text-[11px]" data-testid="ta-lb-reload">
            <RefreshCw size={12} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/60">
            <tr>
              <th className="px-3 py-2 text-left pk-table-header">Employee</th>
              <th className="px-3 py-2 text-left pk-table-header">Department</th>
              <th className="px-3 py-2 text-center pk-table-header">Casual</th>
              <th className="px-3 py-2 text-center pk-table-header">Sick</th>
              <th className="px-3 py-2 text-center pk-table-header">Earned</th>
              <th className="px-3 py-2 text-center pk-table-header">Comp-Off</th>
              <th className="px-3 py-2 text-right pk-table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-6 text-slate-400 text-sm">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-slate-400 text-sm" data-testid="ta-lb-empty">
                No employees found.
              </td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr key={r.id} className="border-b border-slate-100" data-testid={`ta-lb-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-[10px] mono text-slate-500">{r.employee_code || "—"} · {r.email}</div>
                </td>
                <td className="px-3 py-2 text-[12px] text-slate-600">{r.department || "—"}</td>
                <td className="px-3 py-2 text-center mono font-semibold" data-testid={`ta-lb-casual-${r.id}`}>{r.leave_balance?.casual ?? 0}</td>
                <td className="px-3 py-2 text-center mono font-semibold" data-testid={`ta-lb-sick-${r.id}`}>{r.leave_balance?.sick ?? 0}</td>
                <td className="px-3 py-2 text-center mono font-semibold" data-testid={`ta-lb-earned-${r.id}`}>{r.leave_balance?.earned ?? 0}</td>
                <td className="px-3 py-2 text-center mono text-slate-600">{r.comp_off_balance ?? 0}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button onClick={() => openEdit(r)} disabled={busy[r.id]} size="sm" variant="outline"
                      className="h-7 text-[10.5px]" data-testid={`ta-lb-edit-${r.id}`}>
                      <Pencil size={11} className="mr-1" /> Edit
                    </Button>
                    <Button onClick={() => reset(r)} disabled={busy[r.id]} size="sm" variant="outline"
                      className="h-7 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50"
                      data-testid={`ta-lb-reset-${r.id}`}>
                      Reset
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="ta-lb-edit-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Edit Leave Balance — {editing?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Casual</Label>
              <Input type="number" min="0" value={form.casual} onChange={(e) => setForm({ ...form, casual: e.target.value })}
                className="mt-1.5 h-10 mono" data-testid="ta-lb-input-casual" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Sick</Label>
              <Input type="number" min="0" value={form.sick} onChange={(e) => setForm({ ...form, sick: e.target.value })}
                className="mt-1.5 h-10 mono" data-testid="ta-lb-input-sick" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Earned</Label>
              <Input type="number" min="0" value={form.earned} onChange={(e) => setForm({ ...form, earned: e.target.value })}
                className="mt-1.5 h-10 mono" data-testid="ta-lb-input-earned" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="ta-lb-save">
              Save Balance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
